import { componentInterface } from '@ardoq/component-interface';
import { componentOperations } from '@ardoq/core';
import type {
  APIComponentAttributes,
  APIReferenceAttributes,
  ArdoqId,
} from '@ardoq/api-types';

export type ReferenceComponents = {
  references: APIReferenceAttributes[];
  sourceComponentsById: Record<ArdoqId, APIComponentAttributes>;
  targetComponentsById: Record<ArdoqId, APIComponentAttributes>;
  fullPathNamesById: Record<ArdoqId, string>;
};

export type ReferenceEndpoints = {
  source: APIComponentAttributes | null;
  target: APIComponentAttributes | null;
};

export const buildReferenceComponents = (
  references: APIReferenceAttributes[]
): ReferenceComponents =>
  references.reduce<ReferenceComponents>(
    (referenceComponents, reference) => {
      const sourceComponent = componentInterface.get(reference.source);
      if (sourceComponent) {
        referenceComponents.sourceComponentsById[reference.source] =
          sourceComponent;
      }
      const targetComponent = componentInterface.get(reference.target);
      if (targetComponent) {
        referenceComponents.targetComponentsById[reference.target] =
          targetComponent;
      }
      for (const componentId of [reference.source, reference.target]) {
        const fullPathName = componentInterface.getFullPathName(componentId);
        if (fullPathName !== null) {
          referenceComponents.fullPathNamesById[componentId] = fullPathName;
        }
      }
      return referenceComponents;
    },
    {
      references,
      sourceComponentsById: {},
      targetComponentsById: {},
      fullPathNamesById: {},
    }
  );

export const toReferenceComponents = (
  references: APIReferenceAttributes[],
  componentsById: Record<ArdoqId, APIComponentAttributes>
): ReferenceComponents =>
  references.reduce<ReferenceComponents>(
    (referenceComponents, { source, target }) => {
      const sourceComponent = componentsById[source];
      if (sourceComponent) {
        referenceComponents.sourceComponentsById[source] = sourceComponent;
      }
      const targetComponent = componentsById[target];
      if (targetComponent) {
        referenceComponents.targetComponentsById[target] = targetComponent;
      }
      for (const componentId of [source, target]) {
        const fullPathName = componentOperations.getFullPathName(
          componentsById,
          componentId
        );
        if (fullPathName !== null) {
          referenceComponents.fullPathNamesById[componentId] = fullPathName;
        }
      }
      return referenceComponents;
    },
    {
      references,
      sourceComponentsById: {},
      targetComponentsById: {},
      fullPathNamesById: {},
    }
  );
